import * as THREE from "three/webgpu";
import {SketchEntityKind} from '../../sketch/solver/constraint/relation-compatibility/SketchEntityKinds';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {CollectSource, SlotControlPointRole} from './ISketchObject';
import type {ISketchCollect} from './ISketchObject';
import type {SketchLineObject} from './SketchLineObject';
import type {SketchArcDefinition, SketchArcObject} from './SketchArcObject';
import {SketchRelationContainer} from '../../sketch/solver/constraint/SketchRelationContainer';
import type {SketchPointObject} from './SketchPointObject';
import {SketchSnapshot} from '../meshProxy/SketchSnapshot';
import {SketchContour} from '../meshProxy/SketchContour';
import type {SketchSlotCommand} from '../../draw/SketchSlotDrawingHandler';
import {createSketchCloneContext} from './SketchCloneContext';
import type {SketchCloneContext} from './SketchCloneContext';
import {createSketchEntityPersistentId} from '../../sketch/identity/SketchPersistentIdFactory';
import {disposeSketchContour} from './clearSketchContourDisplays';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../../sketch/config/SketchGeometryToleranceConfig';
import {SketchRelationKind} from '../../sketch/solver/constraint/relation-compatibility/SketchRelationKinds';

/**
 * 槽口共享接合点。
 * 说明：
 * 槽口轮廓由 4 条边首尾相连，相邻边共用同一个 SketchPointObject 实例。
 * 拓扑示意（顺时针方向）：
 * ```
 *   P1 ─── topEdge ───→ P2
 *   ↑                     ↓
 * startCapArc           endCapArc
 *   ↑                     ↓
 *   P4 ←── bottomEdge ── P3
 * ```
 *
 * 共用关系：
 * - p1: topEdge.start   = startCapArc.end
 * - p2: topEdge.end     = endCapArc.start
 * - p3: endCapArc.end   = bottomEdge.start
 * - p4: bottomEdge.end  = startCapArc.start
 */
export interface SlotJunctionPoints {
    /** 接合点 P1（topEdge 起点 / startCapArc 终点）。 */
    p1: SketchPointObject;
    /** 接合点 P2（topEdge 终点 / endCapArc 起点）。 */
    p2: SketchPointObject;
    /** 接合点 P3（endCapArc 终点 / bottomEdge 起点）。 */
    p3: SketchPointObject;
    /** 接合点 P4（bottomEdge 终点 / startCapArc 起点）。 */
    p4: SketchPointObject;
}

/**
 * 槽口圆弧边定义。
 * 用于描述槽口分解后每条圆弧边的几何参数。
 */
export interface SlotArcEdgeDef {
    /** 圆心（世界坐标）。 */
    centerWorld: THREE.Vector3;
    /** 半径。 */
    radius: number;
    /** 起始角（弧度，参考面局部坐标系）。 */
    startAngle: number;
    /** 终止角（弧度，参考面局部坐标系）。 */
    endAngle: number;
    /** 是否顺时针。 */
    clockwise: boolean;
}

/**
 * 槽口边缘分解基础数据。
 * 说明：
 * 1. 将槽口轮廓分解为 4 条独立边；
 * 2. 包含 4 个接合点世界坐标，用于创建共享 SketchPointObject。
 */
interface SlotEdgeDecompositionBase {
    /** 接合点 P1 世界坐标（topEdge 起点 / startCapArc 终点）。 */
    p1World: THREE.Vector3;
    /** 接合点 P2 世界坐标（topEdge 终点 / endCapArc 起点）。 */
    p2World: THREE.Vector3;
    /** 接合点 P3 世界坐标（endCapArc 终点 / bottomEdge 起点）。 */
    p3World: THREE.Vector3;
    /** 接合点 P4 世界坐标（bottomEdge 终点 / startCapArc 起点）。 */
    p4World: THREE.Vector3;
    /** 终止端帽弧定义。 */
    endCapArc: SlotArcEdgeDef;
    /** 起始端帽弧定义。 */
    startCapArc: SlotArcEdgeDef;
}

/**
 * 直槽口边缘分解数据。
 * 上下为直线，左右为圆弧端帽。
 */
export interface StraightSlotEdgeDecomposition extends SlotEdgeDecompositionBase {
    /** 分解类型：直槽口。 */
    type: 'straight';
}

/**
 * 圆弧槽口边缘分解数据。
 * 4 条边均为圆弧（外弧、内弧、起始端帽、终止端帽）。
 */
export interface ArcSlotEdgeDecomposition extends SlotEdgeDecompositionBase {
    /** 分解类型：圆弧槽口。 */
    type: 'arc';
    /** 外弧定义（P1 → P2）。 */
    outerArc: SlotArcEdgeDef;
    /** 内弧定义（P3 → P4）。 */
    innerArc: SlotArcEdgeDef;
}

/**
 * 槽口边缘分解数据（联合类型）。
 */
export type SlotEdgeDecomposition = StraightSlotEdgeDecomposition | ArcSlotEdgeDecomposition;

/**
 * 直槽口构造参数。
 * 上下为直线 + 左右为圆弧端帽。
 */
export interface StraightSlotObjectOptions {
    /** 槽口绘制模式。 */
    mode: SketchSlotCommand;
    /** 槽口所属参考面。 */
    referencePlane: ReferencePlane;
    /** 顶部直线边（P1 → P2）。 */
    topLine: SketchLineObject;
    /** 底部直线边（P3 → P4）。 */
    bottomLine: SketchLineObject;
    /** 起始端帽弧（P4 → P1）。 */
    startCapArc: SketchArcObject;
    /** 终止端帽弧（P2 → P3）。 */
    endCapArc: SketchArcObject;
    /** 4 个共享接合点（相邻边共用同一 SketchPointObject 实例）。 */
    junctionPoints: SlotJunctionPoints;
    /** 中心线起始控制点（中心线左端）。 */
    centerlineStartPoint: SketchPointObject;
    /** 中心线终止控制点（中心线右端）。 */
    centerlineEndPoint: SketchPointObject;
    /** 中心控制点（中心线中点）。 */
    centerPoint: SketchPointObject;
    /** 构造中心线（连接 centerlineStartPoint 与 centerlineEndPoint）。 */
    constructionCenterline: SketchLineObject;
}

/**
 * 圆弧槽口构造参数。
 * 4 条边均为圆弧。
 */
export interface ArcSlotObjectOptions {
    /** 槽口绘制模式。 */
    mode: SketchSlotCommand;
    /** 槽口所属参考面。 */
    referencePlane: ReferencePlane;
    /** 外弧边（P1 → P2）。 */
    outerArc: SketchArcObject;
    /** 内弧边（P3 → P4）。 */
    innerArc: SketchArcObject;
    /** 起始端帽弧（P4 → P1）。 */
    startCapArc: SketchArcObject;
    /** 终止端帽弧（P2 → P3）。 */
    endCapArc: SketchArcObject;
    /** 4 个共享接合点（相邻边共用同一 SketchPointObject 实例）。 */
    junctionPoints: SlotJunctionPoints;
    /** 圆弧中心控制点（圆弧槽口的弧线圆心）。 */
    arcCenterPoint: SketchPointObject;
    /** 中心线弧中点控制点（中心线弧角度中点处）。 */
    centerArcMidpoint: SketchPointObject;
    /** 构造中心线弧（连接 startCapArc 圆心与 endCapArc 圆心，虚线样式）。 */
    constructionCenterlineArc: SketchArcObject;
}

/**
 * {@link SketchSlotObject} 构造参数（联合类型）。
 */
export type SketchSlotObjectOptions = StraightSlotObjectOptions | ArcSlotObjectOptions;

/**
 * 直槽口参数更新数据。
 * 调用 {@link SketchSlotObject.updateStraightSlotGeometry} 时传入。
 */
export interface StraightSlotUpdateParams {
    /** 接合点 P1 新世界坐标。 */
    p1World: THREE.Vector3;
    /** 接合点 P2 新世界坐标。 */
    p2World: THREE.Vector3;
    /** 接合点 P3 新世界坐标。 */
    p3World: THREE.Vector3;
    /** 接合点 P4 新世界坐标。 */
    p4World: THREE.Vector3;
    /** 中心轴起始端（startCenter）世界坐标。 */
    startCenterWorld: THREE.Vector3;
    /** 中心轴终止端（endCenter）世界坐标。 */
    endCenterWorld: THREE.Vector3;
    /** 中心点世界坐标（startCenter 与 endCenter 中点）。 */
    centerWorld: THREE.Vector3;
    /** 半宽（width / 2）。 */
    halfWidth: number;
    /** 中心轴方向角（弧度，Math.atan2(axisDir.y, axisDir.x)）。 */
    theta: number;
}

/**
 * 圆弧槽口参数更新数据。
 * 调用 {@link SketchSlotObject.updateArcSlotGeometry} 时传入。
 */
export interface ArcSlotUpdateParams {
    /** 弧线圆心世界坐标。 */
    centerWorld: THREE.Vector3;
    /** 中心线弧半径。 */
    radius: number;
    /** 半宽（width / 2）。 */
    halfWidth: number;
    /** 中心线弧起始角（弧度，参考面局部坐标系）。 */
    startAngle: number;
    /** 中心线弧终止角（弧度，参考面局部坐标系）。 */
    endAngle: number;
    /** 是否顺时针。 */
    clockwise: boolean;
    /** 接合点 P1 新世界坐标。 */
    p1World: THREE.Vector3;
    /** 接合点 P2 新世界坐标。 */
    p2World: THREE.Vector3;
    /** 接合点 P3 新世界坐标。 */
    p3World: THREE.Vector3;
    /** 接合点 P4 新世界坐标。 */
    p4World: THREE.Vector3;
    /** 起始端帽弧圆心世界坐标。 */
    startCapCenterWorld: THREE.Vector3;
    /** 终止端帽弧圆心世界坐标。 */
    endCapCenterWorld: THREE.Vector3;
    /** 中心线弧中点控制点世界坐标。 */
    centerArcMidpointWorld: THREE.Vector3;
}

/**
 * 判断对象是否为草图槽口对象。
 * @param value 待判断的 Three 对象。
 * @returns 当对象满足草图槽口协议时返回 true。
 */


/**
 * 槽口参数记录中**由几何推导**的键。
 *
 * 写入 `geometryData` 时按本表丢弃：形状只有几何一份真相，允许写进来就等于允许它分叉。
 */
const SLOT_DERIVED_PARAMETER_KEYS: ReadonlySet<string> = new Set([
    /** 绘制模式由 `_mode` 承载；`lengthType` 是真定义量，不在此列。 */
    'mode',
    'center', 'centerlineStart', 'centerlineEnd',
    'width', 'centerLength', 'overallLength', 'displayLength', 'angle',
    'radius', 'startAngle', 'endAngle', 'clockwise',
    'planeOrigin', 'normal', 'xAxis', 'yAxis',
    /** 轮廓折线：全仓只写不读，随派生键一并丢弃。 */
    'points',
]);

/**
 * 草图槽口网格对象。
 * 职责：
 * 1. 继承 THREE.Group，作为槽口实体的容器；
 * 2. 管理固定的 4 条边：
 *    - 直槽口：topLine(直线) + bottomLine(直线) + startCapArc(圆弧) + endCapArc(圆弧)；
 *    - 圆弧槽口：outerArc(外弧) + innerArc(内弧) + startCapArc(圆弧) + endCapArc(圆弧)；
 * 3. 维护 4 个共享接合点，相邻边共用同一 SketchPointObject 实例；
 * 4. 实现 ISketchObject 接口，参与约束求解与拖拽留影。
 */
export class SketchSlotObject extends THREE.Group implements ISketchCollect {
    /** 运行时类型标记。 */
    public readonly isSketchSlotObject: boolean = true;
    /** 草图对象标记。 */
    public readonly isSketchObject: boolean = true;
    /** 跨会话稳定持久 ID；`cloneSelf` 继承，复制出新实体的流程必须重新生成。 */
    public persistentId: string = createSketchEntityPersistentId();
    /** 槽口对象属于封闭几何。 */
    public readonly isClosedGeometry: boolean = true;
    /** 对象运行时类型。 */
    public readonly objectType: SketchEntityKind = SketchEntityKind.Slot;
    /** 槽口所属参考面。 */
    public readonly referencePlane: ReferencePlane;

    /** 拖动留影代理。 */
    public readonly dragSnapshotProxy: SketchSnapshot = new SketchSnapshot();

    /**
     * 槽口定义数据（**只存放推不出来的那部分**）。
     *
     * 形状量一律不入此表，见 `geometryData` 访问器说明。
     */
    private slotDefinition: Record<string, any> = {};

    /** 槽口轮廓对象缓存。 */
    public contour: SketchContour | null = null;

    /** 槽口绘制模式。 */
    private _mode: SketchSlotCommand;

    /** 中心线起始控制点（仅直槽口）。 */
    private _centerlineStartPoint: SketchPointObject | null = null;

    /** 中心线终止控制点（仅直槽口）。 */
    private _centerlineEndPoint: SketchPointObject | null = null;

    /** 中心控制点（仅直槽口，中心线中点）。 */
    private _centerPoint: SketchPointObject | null = null;

    /** 构造中心线（仅直槽口，虚线样式）。 */
    private _constructionCenterline: SketchLineObject | null = null;

    /** 构造中心线弧（仅圆弧槽口，虚线样式）。 */
    private _constructionCenterlineArc: SketchArcObject | null = null;

    /** 圆弧中心控制点（仅圆弧槽口，弧线圆心）。 */
    private _arcCenterPoint: SketchPointObject | null = null;

    /** 中心线弧中点控制点（仅圆弧槽口，中心线弧角度中点处）。 */
    private _centerArcMidpoint: SketchPointObject | null = null;

    /** 顶部直线边（仅直槽口，P1 → P2）。 */
    private _topLine: SketchLineObject | null = null;

    /** 底部直线边（仅直槽口，P3 → P4）。 */
    private _bottomLine: SketchLineObject | null = null;

    /** 外弧边（仅圆弧槽口，P1 → P2）。 */
    private _outerArc: SketchArcObject | null = null;

    /** 内弧边（仅圆弧槽口，P3 → P4）。 */
    private _innerArc: SketchArcObject | null = null;

    /** 起始端帽弧（P4 → P1）。 */
    private _startCapArc: SketchArcObject;

    /** 终止端帽弧（P2 → P3）。 */
    private _endCapArc: SketchArcObject;

    /** 4 个共享接合点。 */
    private _junctionPoints: SlotJunctionPoints;

    /**
     * 约束容器。
     *
     * 槽口是可以直接承载几何关系的实体（SW 的`固定槽口`/`相等槽口`就挂在槽口上）。
     * 求解快照采集与持久化序列化都按鸭子类型读取本字段，容器一到位即自动接入两侧。
     */
    public readonly constraints = new SketchRelationContainer(this);

    /** 实例计数，用于生成显示名称。 */
    static count = 0;
    collectSet: Set<CollectSource> = new Set();

    /**
     * 构造草图槽口对象。
     * @param options 构造参数（直槽口或圆弧槽口）。
     */
    public constructor(options: SketchSlotObjectOptions) {
        super();
        this._mode = options.mode;
        this.referencePlane = options.referencePlane;
        this._startCapArc = options.startCapArc;
        this._endCapArc = options.endCapArc;
        this._junctionPoints = options.junctionPoints;

        // 根据模式分别初始化边。
        if ('topLine' in options) {
            // 直槽口：上下为直线。
            this._topLine = options.topLine;
            this._bottomLine = options.bottomLine;
            this.add(this._topLine);
            this.add(this._bottomLine);

            // 直槽口：3 个控制点 + 构造中心线。
            this._centerlineStartPoint = options.centerlineStartPoint;
            this._centerlineEndPoint = options.centerlineEndPoint;
            this._centerPoint = options.centerPoint;
            this._constructionCenterline = options.constructionCenterline;
            this.add(this._centerlineStartPoint);
            this.add(this._centerlineEndPoint);
            this.add(this._centerPoint);
            this.add(this._constructionCenterline);
        } else {
            // 圆弧槽口：外弧 + 内弧。
            this._outerArc = options.outerArc;
            this._innerArc = options.innerArc;
            this.add(this._outerArc);
            this.add(this._innerArc);

            // 圆弧槽口：弧线圆心控制点。
            this._arcCenterPoint = options.arcCenterPoint;
            this.add(this._arcCenterPoint);

            // 圆弧槽口：中心线弧中点控制点。
            this._centerArcMidpoint = options.centerArcMidpoint;
            this.add(this._centerArcMidpoint);

            // 圆弧槽口：构造中心线弧。
            this._constructionCenterlineArc = options.constructionCenterlineArc;
            this.add(this._constructionCenterlineArc);
        }
        this.add(this._startCapArc);
        this.add(this._endCapArc);

        this.registerControlPointRoles();

        this.name = `槽口${SketchSlotObject.count}`;
        SketchSlotObject.count += 1;
    }

    /**
     * 登记槽口控制点角色。
     *
     * 设计说明：
     * 1. 角色是槽口的**结构属性**，而不是绘制过程的产物：拖拽策略靠它把"拖某个点"
     *    翻译成"改槽口参数"，缺角色就会退化成通用点拖拽、把槽口拖散；
     * 2. 因此收口在构造函数里，绘制/反序列化/撤销重建/复制四条构造路径自动一致。
     *    此前只有绘制处理器登记，**文档重开后槽口的所有控制点都没有角色**，
     *    表现为"拖任何端点槽口都会错乱"；
     * 3. 端帽弧圆心与中心线端点是同一个点对象（见绘制处理器共享圆心说明），
     *    这里按槽口类型给它们各自的角色。
     */
    private registerControlPointRoles(): void {
        const assign = (point: SketchPointObject | null | undefined, role: SlotControlPointRole): void => {
            if (point) {
                point.slotControlPointRole = role;
            }
        };

        assign(this._junctionPoints.p1, SlotControlPointRole.JunctionP1);
        assign(this._junctionPoints.p2, SlotControlPointRole.JunctionP2);
        assign(this._junctionPoints.p3, SlotControlPointRole.JunctionP3);
        assign(this._junctionPoints.p4, SlotControlPointRole.JunctionP4);

        if (this.isArcSlot()) {
            assign(this._arcCenterPoint, SlotControlPointRole.ArcCenter);
            assign(this._centerArcMidpoint, SlotControlPointRole.CenterArcMidpoint);
            assign(this._startCapArc.getCenterPoint(), SlotControlPointRole.CenterlineArcStart);
            assign(this._endCapArc.getCenterPoint(), SlotControlPointRole.CenterlineArcEnd);

            return;
        }

        assign(this._centerlineStartPoint, SlotControlPointRole.CenterlineStart);
        assign(this._centerlineEndPoint, SlotControlPointRole.CenterlineEnd);
        assign(this._centerPoint, SlotControlPointRole.Center);
    }

    //------ 草图对象复制 开始------
    /**
     * 复制当前槽口对象，返回与源对象完全解耦的新实例。
     * 说明：
     * 1. 槽口内部共享接合点通过复制上下文保持共享关系；
     * 2. 新槽口不会复用旧槽口的边、点、轮廓与材质实例；
     * 3. 复制失败时直接抛错，不做兜底。
     * @param context 复制上下文。
     * @returns 当前槽口对象的独立副本。
     * @throws 当当前模式所需的圆弧或直线内部字段缺失时抛出错误。
     */
    public cloneSelf(context: SketchCloneContext = createSketchCloneContext()): SketchSlotObject {
        // 先复制共享接合点，保证后续边复制可通过上下文复用同一批新点。
        const clonedJunctionPoints: SlotJunctionPoints = {
            p1: this._junctionPoints.p1.cloneSelf(context),
            p2: this._junctionPoints.p2.cloneSelf(context),
            p3: this._junctionPoints.p3.cloneSelf(context),
            p4: this._junctionPoints.p4.cloneSelf(context),
        };

        let clonedSlot: SketchSlotObject;
        if (this.isArcSlot()) {
            if (
                !this._outerArc
                || !this._innerArc
                || !this._arcCenterPoint
                || !this._centerArcMidpoint
                || !this._constructionCenterlineArc
            ) {
                throw new Error('当前槽口圆弧模式字段缺失，无法复制。');
            }
            clonedSlot = new SketchSlotObject({
                mode: this._mode,
                referencePlane: this.referencePlane,
                outerArc: this._outerArc.cloneSelf(context),
                innerArc: this._innerArc.cloneSelf(context),
                startCapArc: this._startCapArc.cloneSelf(context),
                endCapArc: this._endCapArc.cloneSelf(context),
                junctionPoints: clonedJunctionPoints,
                arcCenterPoint: this._arcCenterPoint.cloneSelf(context),
                centerArcMidpoint: this._centerArcMidpoint.cloneSelf(context),
                constructionCenterlineArc: this._constructionCenterlineArc.cloneSelf(context),
            });
        } else {
            if (
                !this._topLine
                || !this._bottomLine
                || !this._centerlineStartPoint
                || !this._centerlineEndPoint
                || !this._centerPoint
                || !this._constructionCenterline
            ) {
                throw new Error('当前槽口直线模式字段缺失，无法复制。');
            }

            clonedSlot = new SketchSlotObject({
                mode: this._mode,
                referencePlane: this.referencePlane,
                topLine: this._topLine.cloneSelf(context),
                bottomLine: this._bottomLine.cloneSelf(context),
                startCapArc: this._startCapArc.cloneSelf(context),
                endCapArc: this._endCapArc.cloneSelf(context),
                junctionPoints: clonedJunctionPoints,
                centerlineStartPoint: this._centerlineStartPoint.cloneSelf(context),
                centerlineEndPoint: this._centerlineEndPoint.cloneSelf(context),
                centerPoint: this._centerPoint.cloneSelf(context),
                constructionCenterline: this._constructionCenterline.cloneSelf(context),
            });
        }

        // 副本表达同一逻辑槽口，必须继承持久身份；复制出新实体的流程负责显式重新生成。
        clonedSlot.persistentId = this.persistentId;
        clonedSlot.position.copy(this.position);
        clonedSlot.quaternion.copy(this.quaternion);
        clonedSlot.scale.copy(this.scale);
        clonedSlot.visible = this.visible;
        clonedSlot.renderOrder = this.renderOrder;
        clonedSlot.frustumCulled = this.frustumCulled;
        clonedSlot.layers.mask = this.layers.mask;

        // 将复制后的边重新绑定到新槽口，确保轮廓联动逻辑生效。
        clonedSlot.getEdges().forEach((edge) => {
            edge.attachClosedCollection(clonedSlot);
        });

        // 构造中心线（直槽口直线 / 圆弧槽口弧线）同属槽口闭合宿主，
        // 复制后必须恢复宿主绑定，与绘制装配路径保持一致（缺失时拖拽留影与宿主解析会漏掉中心线）。
        clonedSlot.constructionCenterline?.attachClosedCollection(clonedSlot);
        clonedSlot.constructionCenterlineArc?.attachClosedCollection(clonedSlot);

        // 只复制定义量；形状量由克隆体自己的几何现算。
        clonedSlot.slotDefinition = structuredClone(this.slotDefinition);
        clonedSlot.collectSet = new Set(clonedSlot.getEdges() as CollectSource[]);
        if (this.contour) {
            clonedSlot.syncContour();
        }

        // 槽口本身是实体级关系目标，复制上下文必须记录顶层槽口副本。
        context.objectMap.set(this.uuid, clonedSlot);

        return clonedSlot;
    }
    //------ 草图对象复制 结束------


    // ─── 只读访问器 ──────────────────────────────────────────────────────────

    /**
     * 获取槽口绘制模式。
     * @returns 当前模式。
     */
    public get mode(): SketchSlotCommand {
        return this._mode;
    }

    /**
     * 获取顶部直线边（仅直槽口有值）。
     * @returns 顶部直线对象，圆弧槽口返回 null。
     */
    public get topLine(): SketchLineObject | null {
        return this._topLine;
    }

    /**
     * 获取底部直线边（仅直槽口有值）。
     * @returns 底部直线对象，圆弧槽口返回 null。
     */
    public get bottomLine(): SketchLineObject | null {
        return this._bottomLine;
    }

    /**
     * 获取外弧边（仅圆弧槽口有值）。
     * @returns 外弧对象，直槽口返回 null。
     */
    public get outerArc(): SketchArcObject | null {
        return this._outerArc;
    }

    /**
     * 获取内弧边（仅圆弧槽口有值）。
     * @returns 内弧对象，直槽口返回 null。
     */
    public get innerArc(): SketchArcObject | null {
        return this._innerArc;
    }

    /**
     * 获取起始端帽弧。
     * @returns 起始端帽弧对象。
     */
    public get startCapArc(): SketchArcObject {
        return this._startCapArc;
    }

    /**
     * 获取终止端帽弧。
     * @returns 终止端帽弧对象。
     */
    public get endCapArc(): SketchArcObject {
        return this._endCapArc;
    }

    /**
     * 获取中心线起始控制点（仅直槽口有值）。
     * @returns 中心线起始控制点，圆弧槽口返回 null。
     */
    public get centerlineStartPoint(): SketchPointObject | null {
        return this._centerlineStartPoint;
    }

    /**
     * 获取中心线终止控制点（仅直槽口有值）。
     * @returns 中心线终止控制点，圆弧槽口返回 null。
     */
    public get centerlineEndPoint(): SketchPointObject | null {
        return this._centerlineEndPoint;
    }

    /**
     * 获取中心控制点（仅直槽口有值）。
     * @returns 中心控制点，圆弧槽口返回 null。
     */
    public get centerPoint(): SketchPointObject | null {
        return this._centerPoint;
    }

    /**
     * 获取构造中心线（仅直槽口有值）。
     * @returns 构造中心线对象，圆弧槽口返回 null。
     */
    public get constructionCenterline(): SketchLineObject | null {
        return this._constructionCenterline;
    }

    /**
     * 获取构造中心线弧（仅圆弧槽口有值）。
     * @returns 构造中心线弧对象，直槽口返回 null。
     */
    public get constructionCenterlineArc(): SketchArcObject | null {
        return this._constructionCenterlineArc;
    }

    /**
     * 获取圆弧中心控制点（仅圆弧槽口有值）。
     * @returns 弧线圆心控制点，直槽口返回 null。
     */
    public get arcCenterPoint(): SketchPointObject | null {
        return this._arcCenterPoint;
    }

    /**
     * 获取中心线弧中点控制点（仅圆弧槽口有值）。
     * @returns 中心线弧中点控制点，直槽口返回 null。
     */
    public get centerArcMidpoint(): SketchPointObject | null {
        return this._centerArcMidpoint;
    }

    /**
     * 获取 4 个共享接合点。
     * @returns 接合点对象。
     */
    public get junctionPoints(): SlotJunctionPoints {
        return this._junctionPoints;
    }

    //------ 槽口构造线状态 开始------
    /**
     * 获取当前槽口是否作为构造线。
     * 说明：
     * 1. 槽口本体没有独立线型，统一以第一条边的构造线状态作为槽口状态；
     * 2. 若槽口边数量异常为 0，直接抛错，不做兜底；
     * 3. 该状态由内部边对象统一保持一致。
     * @returns 当前槽口是否为构造线。
     * @throws 当槽口不包含任何边对象时抛出错误。
     */
    public get isConstruction(): boolean {
        const firstEdge = this.getEdges()[0];
        if (!firstEdge) {
            throw new Error('槽口边对象缺失，无法读取构造线状态。');
        }
        return firstEdge.isConstruction;
    }

    /**
     * 设置当前槽口是否作为构造线。
     * 说明：
     * 1. 设置时会同步所有边对象；
     * 2. 直槽口的构造中心线始终保持构造线状态；
     * 3. 不修改尺寸辅助线与中心线 helper。
     * @param value 新的构造线状态。
     */
    public set isConstruction(value: boolean) {
        this.getEdges().forEach((edge) => {
            edge.isConstruction = value;
        });
        // 构造中心线（直槽口直线 / 圆弧槽口弧线）始终保持构造线状态。
        if (this._constructionCenterline) {
            this._constructionCenterline.isConstruction = true;
        }
        if (this._constructionCenterlineArc) {
            this._constructionCenterlineArc.isConstruction = true;
        }
    }
    //------ 槽口构造线状态 结束------

    // ─── 边集合访问 ──────────────────────────────────────────────────────────

    /**
     * 获取所有边列表（按拓扑顺序：顶部 → 终止帽 → 底部 → 起始帽）。
     * @returns 边对象只读数组。
     */
    public getEdges(): readonly (SketchLineObject | SketchArcObject)[] {
        if (this.isArcSlot()) {
            return [this._outerArc!, this._endCapArc, this._innerArc!, this._startCapArc];
        }
        return [this._topLine!, this._endCapArc, this._bottomLine!, this._startCapArc];
    }

    /**
     * 获取所有圆弧边。
     * @returns 圆弧对象数组。
     */
    public getArcs(): SketchArcObject[] {
        if (this.isArcSlot()) {
            // 圆弧槽口：4 条边均为圆弧。
            return [this._outerArc!, this._innerArc!, this._startCapArc, this._endCapArc];
        }
        // 直槽口：仅端帽弧。
        return [this._startCapArc, this._endCapArc];
    }

    /**
     * 获取所有直线边（仅直槽口有值）。
     * @returns 直线对象数组；圆弧槽口返回空数组。
     */
    public getLines(): SketchLineObject[] {
        if (this.isArcSlot()) {
            return [];
        }
        return [this._topLine!, this._bottomLine!];
    }

    /**
     * 获取 4 个不重复的共享接合点数组。
     * @returns 接合点数组（按拓扑顺序 P1→P2→P3→P4）。
     */
    public getJunctionPointArray(): SketchPointObject[] {
        const jp = this._junctionPoints;
        return [jp.p1, jp.p2, jp.p3, jp.p4];
    }

    //------ 草图对象拖拽能力判定 开始------
    /**
     * 判断当前槽口对象是否允许拖动。
     * 说明：
     * 1. 槽口当前没有自身独立约束容器，因此只检查内部边与控制点；
     * 2. 任一边对象不可拖动时，整个槽口不可拖动；
     * 3. 任一关键控制点固定时，同样禁止整体拖动。
     * @returns 允许拖动返回 true，否则返回 false。
     */
    public canDrag(): boolean {
        if (
            this.constraints.hasType(SketchRelationKind.Fix)
            || this.constraints.hasType(SketchRelationKind.FixSlot)
        ) {
            return false;
        }
        if (!this.getEdges().every((edge) => edge.canDrag())) {
            return false;
        }

        const checkedPointIds = new Set<string>();
        const pointsToCheck: SketchPointObject[] = [
            ...this.getJunctionPointArray(),
            this._centerlineStartPoint,
            this._centerlineEndPoint,
            this._centerPoint,
            this._arcCenterPoint,
            this._centerArcMidpoint,
        ].filter((point): point is SketchPointObject => point !== null);

        return !pointsToCheck.some((point) => {
            if (checkedPointIds.has(point.uuid)) {
                return false;
            }
            checkedPointIds.add(point.uuid);
            return point.isFixed();
        });
    }
    //------ 草图对象拖拽能力判定 结束------

    /**
     * 判断当前槽口是否为圆弧槽口。
     * @returns 当模式为三点圆弧或中心圆弧时返回 true。
     */
    public isArcSlot(): boolean {
        // 使用字符串匹配避免直接导入枚举产生循环依赖。
        return this._mode === 'sketch-slot-3-point-arc' || this._mode === 'sketch-slot-center-arc';
    }


    //------ 直槽口参数更新 开始------

    /**
     * 直槽口参数更新入口。
     * 根据新的中心轴端点和宽度，重新定位所有接合点、边、控制点和构造中心线。
     * @param params 更新参数。
     */
    public updateStraightSlotGeometry(params: StraightSlotUpdateParams): void {
        if (this.isArcSlot()) {
            return;
        }

        const {
            p1World, p2World, p3World, p4World,
            startCenterWorld, endCenterWorld, centerWorld,
            halfWidth, theta,
        } = params;

        // 更新 4 个接合点位置。
        const jp = this._junctionPoints;
        jp.p1.setPosition(p1World);
        jp.p2.setPosition(p2World);
        jp.p3.setPosition(p3World);
        jp.p4.setPosition(p4World);

        // 更新上下直线几何。
        this._topLine?.syncEndpointsToGeometry();
        this._bottomLine?.syncEndpointsToGeometry();

        // 更新端帽弧定义。
        const startCapDef: SketchArcDefinition = {
            centerWorld: startCenterWorld.clone(),
            radius: halfWidth,
            startAngle: theta - Math.PI / 2,
            endAngle: theta + Math.PI / 2,
            clockwise: true,
        };
        const endCapDef: SketchArcDefinition = {
            centerWorld: endCenterWorld.clone(),
            radius: halfWidth,
            startAngle: theta + Math.PI / 2,
            endAngle: theta - Math.PI / 2,
            clockwise: true,
        };
        // 更新端帽弧圆心控制点位置。
        this._startCapArc.getCenterPoint().setPosition(startCenterWorld.clone());
        this._endCapArc.getCenterPoint().setPosition(endCenterWorld.clone());
        // 重建端帽弧几何。
        this._startCapArc.updateArcDefinition(startCapDef);
        this._endCapArc.updateArcDefinition(endCapDef);

        // 更新构造中心线和控制点。
        if (this._centerlineStartPoint) {
            this._centerlineStartPoint.setPosition(startCenterWorld.clone());
        }
        if (this._centerlineEndPoint) {
            this._centerlineEndPoint.setPosition(endCenterWorld.clone());
        }
        if (this._centerPoint) {
            this._centerPoint.setPosition(centerWorld.clone());
        }
        // 刷新构造中心线几何。
        this._constructionCenterline?.syncEndpointsToGeometry();
    }

    //------ 直槽口参数更新 结束------

    //------ 圆弧槽口参数更新 开始------

    /**
     * 圆弧槽口参数更新入口。
     * 根据新的弧线参数，重新定位所有接合点、4 条弧边、控制点。
     * @param params 更新参数。
     */
    public updateArcSlotGeometry(params: ArcSlotUpdateParams): void {
        if (!this.isArcSlot()) {
            return;
        }

        const {
            centerWorld, radius, halfWidth,
            startAngle, endAngle, clockwise,
            p1World, p2World, p3World, p4World,
            startCapCenterWorld, endCapCenterWorld,
            centerArcMidpointWorld,
        } = params;

        const outerRadius = radius + halfWidth;
        const innerRadius = Math.max(radius - halfWidth, SKETCH_GEOMETRY_TOLERANCE_CONFIG.slotMinimumRadius);

        // 更新 4 个接合点位置。
        const jp = this._junctionPoints;
        jp.p1.setPosition(p1World);
        jp.p2.setPosition(p2World);
        jp.p3.setPosition(p3World);
        jp.p4.setPosition(p4World);

        // 更新外弧定义（P1 → P2，与中心线弧同方向）。
        const outerArcDef: SketchArcDefinition = {
            centerWorld: centerWorld.clone(),
            radius: outerRadius,
            startAngle,
            endAngle,
            clockwise,
        };
        this._outerArc!.getCenterPoint().setPosition(centerWorld.clone());
        this._outerArc!.updateArcDefinition(outerArcDef);

        // 更新内弧定义（P3 → P4，与中心线弧反方向）。
        const innerArcDef: SketchArcDefinition = {
            centerWorld: centerWorld.clone(),
            radius: innerRadius,
            startAngle: endAngle,
            endAngle: startAngle,
            clockwise: !clockwise,
        };
        this._innerArc!.getCenterPoint().setPosition(centerWorld.clone());
        this._innerArc!.updateArcDefinition(innerArcDef);

        // 更新终止端帽弧定义（P2 → P3，以 endCapCenter 为圆心）。
        const endCapDef: SketchArcDefinition = {
            centerWorld: endCapCenterWorld.clone(),
            radius: halfWidth,
            startAngle: endAngle,
            endAngle: endAngle + Math.PI,
            clockwise,
        };
        this._endCapArc.getCenterPoint().setPosition(endCapCenterWorld.clone());
        this._endCapArc.updateArcDefinition(endCapDef);

        // 更新起始端帽弧定义（P4 → P1，以 startCapCenter 为圆心）。
        const startCapDef: SketchArcDefinition = {
            centerWorld: startCapCenterWorld.clone(),
            radius: halfWidth,
            startAngle: startAngle + Math.PI,
            endAngle: startAngle,
            clockwise,
        };
        this._startCapArc.getCenterPoint().setPosition(startCapCenterWorld.clone());
        this._startCapArc.updateArcDefinition(startCapDef);

        // 更新弧线圆心控制点。
        if (this._arcCenterPoint) {
            this._arcCenterPoint.setPosition(centerWorld.clone());
        }

        // 更新构造中心线弧定义（圆心复用槽口圆心，端点复用端帽弧圆心共享点）。
        this._constructionCenterlineArc?.updateArcDefinition({
            centerWorld: centerWorld.clone(),
            radius,
            startAngle,
            endAngle,
            clockwise,
        });

        // 更新中心线弧中点控制点。
        if (this._centerArcMidpoint) {
            this._centerArcMidpoint.setPosition(centerArcMidpointWorld.clone());
        }
    }

    //------ 圆弧槽口参数更新 结束------

    //------ 槽口参数记录 开始------

    /**
     * 槽口参数记录（形状量**每次读取时由几何现算**，不落存储）。
     *
     * 设计说明：
     * 1. 槽口的形状只有一份真相——它自己的点与弧。参数记录曾是与之并存的第二份表示，
     *    由各个写者各自维护；只要有一个写者漏更新（松手裁决、参数求解写回都漏过），
     *    记录就与几何分叉，而下一次拖拽正是拿记录当起手值，于是槽口跳回上一次的中间态。
     *    改为读时现算后，"不同步"这件事在结构上不再可能发生。
     * 2. 存储里只留推不出来的定义量（`mode` 由 `_mode` 承载，故仅 `lengthType`、
     *    尺寸锚点等留在 `slotDefinition`）；写入本属性时派生键一律丢弃，
     *    杜绝调用方以为"写进去了"。
     * 3. 参考面退化（未装配完成）时返回定义量原样，调用方按缺字段处理。
     */
    public get geometryData(): Record<string, any> | null {
        const merged: Record<string, any> = {...this.slotDefinition, ...this.deriveShapeParameters()};

        /**
         * 键按字典序输出：持久化要求同一份草图序列化结果逐字节确定，
         * 而合并顺序会随"这个槽口是新建的还是读盘重建的"而变（存过的定义量排在前面）。
         */
        return Object.fromEntries(
            Object.entries(merged).sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0)),
        );
    }

    public set geometryData(value: Record<string, any> | null) {
        const next: Record<string, any> = {};

        Object.entries(value ?? {}).forEach(([key, item]) => {
            if (!SLOT_DERIVED_PARAMETER_KEYS.has(key)) {
                next[key] = item;
            }
        });
        this.slotDefinition = next;
    }

    /**
     * 由当前几何推导槽口形状参数。
     *
     * @returns 形状参数；几何不完整时返回空表。
     */
    private deriveShapeParameters(): Record<string, any> {
        const referencePlane = this.referencePlane;

        if (!referencePlane) {
            return {};
        }

        const lengthType = this.slotDefinition.lengthType === 'overall' ? 'overall' : 'center-center';
        const width = this._startCapArc.getArcDefinition().radius * 2;
        const readLocal = (point: SketchPointObject): THREE.Vector2 => {
            const local = point.getLocalPosition2D();

            return new THREE.Vector2(local.x, local.y);
        };
        const shape: Record<string, any> = {mode: this._mode, lengthType};
        let centerLocal: THREE.Vector2;
        let startCenterLocal: THREE.Vector2;
        let endCenterLocal: THREE.Vector2;
        let centerLength: number;

        if (this.isArcSlot()) {
            if (!this._arcCenterPoint || !this._centerArcMidpoint || !this._outerArc) {
                return {};
            }
            centerLocal = readLocal(this._arcCenterPoint);
            startCenterLocal = readLocal(this._startCapArc.getCenterPoint());
            endCenterLocal = readLocal(this._endCapArc.getCenterPoint());

            const radius = readLocal(this._centerArcMidpoint).sub(centerLocal).length();
            const clockwise = this._outerArc.getArcDefinition().clockwise;
            const startDirection = startCenterLocal.clone().sub(centerLocal);
            const endDirection = endCenterLocal.clone().sub(centerLocal);
            const startAngle = Math.atan2(startDirection.y, startDirection.x);
            const rawEndAngle = Math.atan2(endDirection.y, endDirection.x);
            /** 有符号扫角按外弧走向展开到 (0, 2π]，与求解快照同一套推导（含优角）。 */
            let sweep = clockwise ? startAngle - rawEndAngle : rawEndAngle - startAngle;

            sweep %= Math.PI * 2;
            if (sweep <= 0) {
                sweep += Math.PI * 2;
            }
            centerLength = radius * sweep;

            shape.radius = radius;
            shape.startAngle = startAngle;
            shape.endAngle = startAngle + (clockwise ? -sweep : sweep);
            shape.clockwise = clockwise;
            shape.angle = THREE.MathUtils.radToDeg(sweep);
        } else {
            if (!this._centerlineStartPoint || !this._centerlineEndPoint) {
                return {};
            }
            startCenterLocal = readLocal(this._centerlineStartPoint);
            endCenterLocal = readLocal(this._centerlineEndPoint);
            centerLocal = startCenterLocal.clone().add(endCenterLocal).multiplyScalar(0.5);
            centerLength = startCenterLocal.distanceTo(endCenterLocal);

            const axis = endCenterLocal.clone().sub(startCenterLocal);

            shape.angle = THREE.MathUtils.radToDeg(Math.atan2(axis.y, axis.x));
        }

        const overallLength = centerLength + width;

        shape.center = referencePlane.localToWorld(centerLocal).toArray();
        shape.centerlineStart = referencePlane.localToWorld(startCenterLocal).toArray();
        shape.centerlineEnd = referencePlane.localToWorld(endCenterLocal).toArray();
        shape.width = width;
        shape.centerLength = centerLength;
        shape.overallLength = overallLength;
        shape.displayLength = lengthType === 'overall' ? overallLength : centerLength;
        shape.planeOrigin = referencePlane.originWorld.toArray();
        shape.normal = referencePlane.normal.toArray();
        shape.xAxis = referencePlane.xAxis.toArray();
        shape.yAxis = referencePlane.yAxis.toArray();

        return shape;
    }

    //------ 槽口参数记录 结束------

    //------ 槽口轮廓 开始------

    /**
     * 同步槽口轮廓网格。
     * 说明：
     * 1. 每次调用都会重建轮廓，避免轮廓与边缘几何不一致；
     * 2. 若已存在旧轮廓，会先从分组移除再替换为新轮廓。
     */
    public syncContour(): void {
        if (this.hasConstructionContourEdges()) {
            this.clearContourDisplay();
            return;
        }
        this.clearContourDisplay();
        const contour = this.createContour();
        if (contour) {
            this.contour = contour;
            this.add(contour);
        }
    }

    /**
     * 清理当前轮廓显示。
     * 说明：
     * 1. 仅移除当前槽口对象上挂载的轮廓实例；
     * 2. 同步清空轮廓引用；
     * 3. 不修改槽口边与控制点几何。
     */
    public clearContourDisplay(): void {
        const contour = this.contour;
        if (contour && contour.parent === this) {
            this.remove(contour);
        }
        if (contour) {
            disposeSketchContour(contour);
        }
        this.contour = null;
    }

    /** 判断槽口是否可懒创建轮廓。 */
    public canCreateContour(): boolean {
        return true;
    }

    /**
     * 获取或懒创建槽口轮廓对象，避免拉伸依赖 UI 是否已显示着色轮廓。
     * @returns 现有或新创建的槽口轮廓对象。
     * @throws 当槽口含构造边而无法创建轮廓时抛出错误。
     */
    public getContour(): SketchContour {
        if (!this.contour) {
            const contour = this.createContour();
            if (!contour) {
                throw new Error('槽口轮廓创建失败，无法继续解析拉伸 profile。');
            }
            this.contour = contour;
        }
        return this.contour;
    }

    /** 基于当前槽口所有边创建轮廓对象。 */
    public createContour(): SketchContour | null {
        if (this.hasConstructionContourEdges()) {
            return null;
        }
        const contour = new SketchContour({
            source: this,
        });
        this.contour = contour;
        return contour;
    }

    /**
     * 刷新已创建轮廓的几何（保持轮廓对象实例不变）。
     * 用于拖拽过程中实时同步上色轮廓。
     */
    public refreshContourGeometry(): void {
        if (!this.contour) {
            return;
        }
        if (this.hasConstructionContourEdges()) {
            this.clearContourDisplay();
            return;
        }
        const points = this.collectGeometryPoints();
        if (points.length >= 3) {
            this.contour.updatePoints(points);
        }
    }

    /**
     * 判断槽口组成边中是否包含构造边。
     * @returns 只要任一边是构造线即返回 true。
     */
    private hasConstructionContourEdges(): boolean {
        return this.getEdges().some((edge) => edge.isConstruction);
    }

    /**
     * 按拓扑顺序收集所有边的几何顶点（P1→P2 via topEdge, P2→P3 via endCapArc, P3→P4 via bottomEdge, P4→P1 via startCapArc）。
     * @returns 顶点世界坐标列表。
     */
    private collectGeometryPoints(): THREE.Vector3[] {
        const points: THREE.Vector3[] = [];
        // 按拓扑顺序遍历 4 条边：top/outerArc → endCapArc → bottom/innerArc → startCapArc。
        const edges = this.getEdges();
        for (const edge of edges) {
            const position = edge.geometry.getAttribute('position');
            if (!position || position.count <= 0) {
                continue;
            }
            // 跳过最后一个点（与下一条边的起点重合）。
            const count = position.count - 1;
            for (let i = 0; i < count; i++) {
                points.push(new THREE.Vector3(
                    position.getX(i),
                    position.getY(i),
                    position.getZ(i),
                ));
            }
        }
        return points;
    }

    //------ 槽口轮廓 结束------

    /**
     * 拖动开始时显示留影。
     * 说明：
     * 相邻边共享接合点，需按 UUID 去重，避免对同一点重复触发留影。
     */
    public beginDragPreview(): void {
        // 先对共享接合点去重触发留影。
        const visitedPointIds = new Set<string>();
        this.getJunctionPointArray().forEach((point) => {
            if (!visitedPointIds.has(point.uuid)) {
                visitedPointIds.add(point.uuid);
                point.beginDragPreview();
            }
        });
        // 再对每条边触发边自身的留影（边内部不会再重复触发已处理的点）。
        const edges = this.getEdges();
        edges.forEach((edge) => {
            edge.dragSnapshotProxy.show(edge);
        });
        this.dragSnapshotProxy.show(this);
    }

    /**
     * 拖动结束时隐藏留影。
     * 说明：
     * 与 beginDragPreview 对称，按 UUID 去重清除共享接合点留影。
     */
    public endDragPreview(): void {
        this.dragSnapshotProxy.hide();

        // 清除每条边自身的留影。
        const edges = this.getEdges();
        edges.forEach((edge) => {
            edge.dragSnapshotProxy.hide();
        });
        // 去重清除共享接合点留影。
        const visitedPointIds = new Set<string>();
        this.getJunctionPointArray().forEach((point) => {
            if (!visitedPointIds.has(point.uuid)) {
                visitedPointIds.add(point.uuid);
                point.endDragPreview();
            }
        });
    }
}
